/**
 * Writing and removing a reference-table flag — the one flow, shared by every
 * surface that offers it (the declare dialog's checkbox, the FK-cell popover
 * footer, the enum details dialog's implicit variant, and the MetaInfo
 * manager's "Reference tables" subsection).
 *
 * Unlike a per-column declaration, this is never a local edit: the flag is what
 * makes EVERY column referencing that table an implicit lookup enum, so
 * dropping it takes the names away from all of them at once. That is the whole
 * reason removal goes through the app-level confirm — and the reason the copy
 * says so out loud.
 *
 * Columns carrying their own `EnumColumnDeclaration` are unaffected: an
 * explicit declaration always wins (`resolveEffectiveLookupDeclaration`), so it
 * survives the flag it never depended on.
 *
 * Both events carry `mode`, because the two modes are two different promises:
 * `'cached'` reads the target's whole (capped) pair set into MetaInfo once,
 * `'on-demand'` resolves pairs per visible window and persists nothing.
 */

import type { TrackFunction } from '@/core/analytics/analyticsTypes';
import type { ReferenceTableMode } from '../../utils/effectiveLookupDeclaration';
import { addReferenceTable, removeReferenceTable } from '../../services/metaInfoApi';
import { qualifyLookupTarget } from '../../utils/lookupEnumTarget';
import { openConfirmDialog } from '../confirm/confirmDialogStore';
import type { DeclareEnumColumnDialogTrigger } from '../filters/declareEnumColumnDialogStore';

/** Which surface the user removed the flag from — analytics only. */
export type ReferenceTableUndeclareTrigger =
  | 'details-dialog'
  | 'metainfo-manager'
  /** The FK-cell popover footer's "Stop showing names". */
  | 'fk-popover';

/**
 * Write the flag + fire `feature:enum_reference_table_declare`. Shared so the
 * two entry points (declare dialog → `'cached'`, FK popover → `'on-demand'`)
 * can never drift on the event's shape. Throws whatever the IPC throws — the
 * caller owns its own error surface.
 */
export async function declareReferenceTable(args: {
  connectionId: string;
  database: string;
  /** `''` on schemaless engines (Kusto) — never presence-checked with `!`. */
  schema: string;
  table: string;
  engine?: string;
  /** How this target's pairs will be obtained. Chosen by the entry point. */
  mode: ReferenceTableMode;
  trigger: DeclareEnumColumnDialogTrigger;
  track: TrackFunction;
  invalidate: (connectionId: string, database: string) => void | Promise<void>;
}): Promise<void> {
  const { connectionId, database, schema, table, engine, mode, trigger, track, invalidate } =
    args;
  await addReferenceTable(connectionId, database, {
    schema,
    table,
    createdAt: new Date().toISOString(),
    // Absent `mode` means `'cached'` on disk, so the default stays unwritten —
    // a flag written before this slice and one written by the dialog today are
    // byte-identical records.
    ...(mode === 'cached' ? {} : { mode }),
  });
  track('feature:enum_reference_table_declare', {
    engine: engine ?? 'unknown',
    trigger,
    mode,
  });
  await invalidate(connectionId, database);
}

export function confirmRemoveReferenceTable(args: {
  connectionId: string;
  database: string;
  /** `''` on schemaless engines (Kusto) — never presence-checked with `!`. */
  schema: string;
  table: string;
  engine?: string;
  /** The mode the flag being dropped carried — analytics only. */
  mode: ReferenceTableMode;
  trigger: ReferenceTableUndeclareTrigger;
  track: TrackFunction;
  invalidate: (connectionId: string, database: string) => void | Promise<void>;
  /** Runs after a successful removal — e.g. closing the host dialog. */
  onRemoved?: () => void;
}): void {
  const { connectionId, database, schema, table, engine, mode, trigger, track, invalidate } =
    args;
  const qualified = qualifyLookupTarget(schema, table);
  openConfirmDialog({
    title: `Stop treating ${qualified} as a reference table?`,
    description:
      `Every column referencing ${qualified} shows names from it without a declaration ` +
      `of its own. Removing the flag drops those names everywhere. Columns with their ` +
      `own enum declaration keep theirs.`,
    confirmLabel: 'Stop',
    variant: 'destructive',
    onConfirm: async () => {
      await removeReferenceTable(connectionId, database, schema, table);
      await invalidate(connectionId, database);
      track('feature:enum_reference_table_undeclare', {
        engine: engine ?? 'unknown',
        trigger,
        mode,
      });
      args.onRemoved?.();
    },
  });
}
